import { kioskShell, type KioskShell } from '../config/kioskShell';

import { noteEvent } from '../lib/diagnostics';

/**
 * Pick the kiosk's camera, or refuse.
 *
 * ## Why this file exists
 *
 * The unit enumerates two cameras: a USB webcam (the real one, Camera2 level
 * EXTERNAL) and an internal RK628 HDMI-to-CSI bridge. Opening the bridge with no
 * HDMI signal NULL-derefs in the rk628-csi driver and **kernel-panics the whole
 * device**. MHA hit this and refused to bind it in Kotlin; see
 * `docs/KIOSK_HARDWARE.md` and MHA's `CameraXController.kt:75-92`.
 *
 * In a WebView the camera is opened by `getUserMedia`, so the refusal has to
 * happen here. `facingMode: 'user'` is not a refusal — Chromium maps
 * `LENS_FACING_EXTERNAL` to an *empty* facingMode, so on this unit no camera is
 * "user", the non-exact constraint is dropped, and Chromium opens the default
 * device. Which may be the bridge.
 *
 * ## Why index and not deviceId or label
 *
 * - **Not a baked deviceId.** A Chromium `MediaDeviceInfo.deviceId` is an HMAC
 *   salted per-origin *and* per-WebView-profile. It cannot be computed by the
 *   shell, and a value baked into the bundle goes stale the moment app data is
 *   cleared — after which the kiosk opens nothing until someone rebuilds a 75MB
 *   APK.
 * - **Not a label.** Chromium hides device labels until a camera permission has
 *   actually been exercised, so on first load every label is `''`. A label is a
 *   cross-check here, never the key.
 * - **Index works.** Chromium's `VideoCaptureDeviceFactoryCamera2` walks
 *   `CameraManager.getCameraIdList()` in order, which is the same list the shell
 *   read. So the Nth `videoinput` is the shell's `cameras[N]`.
 *
 * Every path that cannot prove which device it is about to open throws.
 */

export class CameraSelectionError extends Error {
  readonly devices: MediaDeviceInfo[];
  constructor(message: string, devices: MediaDeviceInfo[] = []) {
    super(message);
    this.name = 'CameraSelectionError';
    this.devices = devices;
  }
}

function denied(label: string, patterns: string[]): string | null {
  const lower = label.toLowerCase();
  return patterns.find((p) => lower.includes(p.toLowerCase())) ?? null;
}

/** A human-readable account of what was on the bus and why each was rejected. */
export function describeCameras(
  devices: MediaDeviceInfo[],
  shell: KioskShell | null,
): string {
  if (devices.length === 0) return 'no video inputs enumerated';
  return devices
    .map((d, i) => {
      const c = shell?.cameras[i];
      const label = d.label || '(unlabelled)';
      if (!c) return `[${i}] ${label} — no matching shell entry`;
      return `[${i}] ${label} — ${c.facing}/${c.level} ${c.safe ? 'SAFE' : `REJECTED: ${c.reason}`}`;
    })
    .join('\n');
}

/**
 * Resolve the deviceId of the camera the shell says is safe.
 *
 * @throws CameraSelectionError when it cannot be established. There is
 * deliberately no fallback: on this unit "just use the first one" is how you
 * kernel-panic the board.
 */
/**
 * Make Chromium reveal the real device list, by opening a stream it can only
 * satisfy with the safe camera.
 *
 * Until a `getUserMedia` has actually succeeded in this document, Chromium
 * answers `enumerateDevices()` with a single anonymous placeholder — one entry
 * per *kind*, `deviceId: ''`, `label: ''`. It says nothing about how many
 * cameras exist or which is which. Measured on the unit 2026-09-18; none of the
 * gentler routes lift it (`navigator.permissions` reports `prompt`, an
 * audio-only grant unlocks audio labels only, and a DevTools
 * `Browser.grantPermissions` is accepted and changes nothing).
 *
 * That circularity is the whole problem: you cannot name a camera until you
 * have opened one, and opening an unnamed one is exactly what must not happen
 * here — Chromium's default could be the RK628 bridge, which kernel-panics the
 * board when opened with no HDMI signal. (The unit has no HDMI input, so the
 * bring-up trick of feeding it a live source is not available either.)
 *
 * The way through is a size only the safe camera advertises. Chromium resolves
 * constraints against Camera2 metadata *before* it opens anything — an
 * impossible constraint raises OverconstrainedError with no device touched — so
 * a probe pinned to such a size either opens the safe camera or opens nothing.
 * `exact` matters: a non-exact hint is advisory and Chromium may satisfy it by
 * falling back to the default device, which is the one outcome this must never
 * produce.
 *
 * The sizes come from the shell, which computed them from the hardware actually
 * present rather than from anything baked in here.
 */
async function unlockDeviceList(shell: KioskShell): Promise<void> {
  if (shell.safeCameraProbeSizes.length === 0) {
    throw new CameraSelectionError(
      'the shell published no probe size unique to the safe camera, so there is no way to ' +
        'open it without risking the other one. Refusing. (An APK older than 2026-09-18 ' +
        'does not publish these at all.)',
    );
  }

  const failures: string[] = [];
  for (const size of shell.safeCameraProbeSizes) {
    let stream: MediaStream | undefined;
    try {
      stream = await navigator.mediaDevices.getUserMedia({
        video: { width: { exact: size.width }, height: { exact: size.height } },
      });
      noteEvent(`camera list unlocked by a ${size.width}x${size.height} probe`);
      return;
    } catch (err) {
      failures.push(`${size.width}x${size.height}: ${(err as Error).name}`);
    } finally {
      // Release it either way. The real open happens afterwards with an exact
      // deviceId, and holding this would be a second handle on the same camera.
      stream?.getTracks().forEach((t) => t.stop());
    }
  }

  throw new CameraSelectionError(
    `no probe size could be opened, so the device list stayed anonymous. Tried ${failures.join(
      '; ',
    )}`,
  );
}

export async function selectKioskCamera(): Promise<{ deviceId: string; label: string }> {
  const shell = kioskShell();
  if (!shell) {
    throw new CameraSelectionError(
      'kiosk camera selection requires the native shell (window.__KIOSK__ absent)',
    );
  }

  if (!navigator.mediaDevices?.enumerateDevices) {
    throw new CameraSelectionError('enumerateDevices unavailable — not a secure context?');
  }

  let all = await navigator.mediaDevices.enumerateDevices();
  let cams = all.filter((d) => d.kind === 'videoinput');

  // An anonymised list is the pre-permission placeholder, not a real census: a
  // blank deviceId cannot be opened, and the count is not the camera count. It
  // is also what the shell/page mismatch below was really reporting.
  if (cams.length === 0 || cams.some((d) => d.deviceId === '')) {
    await unlockDeviceList(shell);
    all = await navigator.mediaDevices.enumerateDevices();
    cams = all.filter((d) => d.kind === 'videoinput');
  }

  if (shell.safeCameraIndex < 0) {
    throw new CameraSelectionError(
      `the shell found no safe camera among ${shell.cameraCount}:\n${describeCameras(cams, shell)}`,
      cams,
    );
  }

  /*
   * Select by LABEL, not by enumeration index.
   *
   * This file used to map the shell's Camera2 index onto the page's Nth
   * `videoinput`, on the reasoning that Chromium's
   * VideoCaptureDeviceFactoryCamera2 walks `getCameraIdList()` in the same
   * order. Measured on the unit 2026-09-18, it does not:
   *
   *   shell    cameras[0] = id "0"  EXTERNAL  (the ABKO USB webcam, safe)
   *            cameras[1] = id "2"  BACK      (the RK628 bridge, hazard)
   *   page     videoinput[0] = "camera 1, facing back"       <- the BRIDGE
   *            videoinput[1] = "camera 0, facing external"   <- the webcam
   *
   * Reversed. Had the index mapping been trusted, `safeCameraIndex` 0 would
   * have opened the bridge — which is the exact kernel panic this file exists
   * to prevent. The old deny-pattern check caught it, but only by luck of that
   * check existing; the mapping itself was wrong.
   *
   * Labels are trustworthy here in a way they were not before, because
   * `unlockDeviceList` has already run and Chromium only populates labels once
   * a camera stream has succeeded. So the ordering assumption is dropped
   * entirely, along with the count comparison it needed: what matters is not
   * how many devices each side sees, but that exactly one of them is provably
   * the safe camera.
   */
  const labelled = cams.filter((d) => d.label !== '');
  if (labelled.length === 0) {
    throw new CameraSelectionError(
      `no camera reported a label even after a successful probe, so none can be identified. ` +
        `Refusing.\n${describeCameras(cams, shell)}`,
      cams,
    );
  }

  // Hard veto first, independent of anything the shell said was safe.
  const allowed = labelled.filter((d) => !denied(d.label, shell.denyLabelPatterns));
  const candidates = allowed.filter((d) =>
    shell.safeCameraLabelHints.some((h) => d.label.toLowerCase().includes(h.toLowerCase())),
  );

  if (candidates.length === 0) {
    throw new CameraSelectionError(
      `no camera matched the shell's hints [${shell.safeCameraLabelHints.join(', ')}] once the ` +
        `deny list was applied. Refusing rather than opening an unknown device.\n` +
        `${describeCameras(cams, shell)}`,
      cams,
    );
  }
  if (candidates.length > 1) {
    throw new CameraSelectionError(
      `${candidates.length} cameras matched the shell's hints ` +
        `(${candidates.map((d) => `"${d.label}"`).join(', ')}). Ambiguous, so refusing.`,
      cams,
    );
  }

  const [picked] = candidates;
  if (!picked) {
    throw new CameraSelectionError('camera candidate vanished between checks', cams);
  }
  noteEvent(`camera resolved by label: "${picked.label}"`);

  return { deviceId: picked.deviceId, label: picked.label };
}
